import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';

import BasePayModal from '../../components/modals/BasePayModal';
import TaxWithholdingsModal from '../../components/modals/TaxWithholdingsModal';
import PayTypesDrawer from '../../components/PayTypesDrawer';
import EditPayTypeModal from '../../components/modals/EditPayTypeModal';
import StatHolidayModal from '../../components/modals/StatHolidayModal';
import TimeOffModal from '../../components/modals/TimeOffModal';
import AddDeductionModal from '../../components/modals/AddDeductionModal';
import DentalT4CodeModal from '../../components/modals/DentalT4CodeModal';
import PersonalInfoModal from '../../components/modals/PersonalInfoModal';
import EmergencyContactsModal from '../../components/modals/EmergencyContactsModal';
import EmploymentDetailsModal from '../../components/modals/EmploymentDetailsModal';
import PaymentMethodModal from '../../components/modals/PaymentMethodModal';

import { getCountryConfig, validateField } from '../../utils/countryPayroll';
import { getReadiness } from '../../utils/payrollReadiness';
import apiFetch from '../../utils/apiFetch';

import Rail from './components/Rail';
import Section from './components/Section';
import { useEmployee } from './useEmployee';
import { useModalEvents } from './useModalEvents';
import {
  authHeaders, buildSections, employeeToValues, valuesToPatch,
  initialsFrom, isFilled,
} from './helpers';
import { FONT, API_URL } from './constants';
import { C } from './styles';
import type {
  Country, Employee, Toast, Values, WorkLocation,
} from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const BasePayModalAny = BasePayModal as any;
const TaxWithholdingsModalAny = TaxWithholdingsModal as any;
const PayTypesDrawerAny = PayTypesDrawer as any;
const EditPayTypeModalAny = EditPayTypeModal as any;
const StatHolidayModalAny = StatHolidayModal as any;
const TimeOffModalAny = TimeOffModal as any;
const AddDeductionModalAny = AddDeductionModal as any;
const DentalT4CodeModalAny = DentalT4CodeModal as any;
const PersonalInfoModalAny = PersonalInfoModal as any;
const EmergencyContactsModalAny = EmergencyContactsModal as any;
const EmploymentDetailsModalAny = EmploymentDetailsModal as any;
const PaymentMethodModalAny = PaymentMethodModal as any;
const getCountryConfigAny = getCountryConfig as any;
const validateFieldAny = validateField as any;
const getReadinessAny = getReadiness as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

export default function EmployeeProfileV2() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { employee, isLoading: employeeLoading, error: employeeError, setEmployee, invalidateEmployee, queryClient } = useEmployee(id);

  const [values, setValues] = useState<Values>({});
  const [draft, setDraft] = useState<Values>({});
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [openId, setOpenId] = useState<string | null>('personal');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [companyCountry, setCompanyCountry] = useState<string>('CA');
  const [companyProvince, setCompanyProvince] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  // Modal state (all driven by window custom events via useModalEvents)
  const [basePayModalOpen, setBasePayModalOpen] = useState<boolean>(false);
  const [taxModalOpen, setTaxModalOpen] = useState<boolean>(false);
  const [addPayModalOpen, setAddPayModalOpen] = useState<boolean>(false);
  const [, setAssignedPayTypeIds] = useState<string[]>([]);
  const [editPayModalOpen, setEditPayModalOpen] = useState<boolean>(false);
  const [editingPayItem, setEditingPayItem] = useState<unknown>(null);
  const [statHolidayModalOpen, setStatHolidayModalOpen] = useState<boolean>(false);
  const [statHolidayData, setStatHolidayData] = useState<unknown>(null);
  const [timeOffModalOpen, setTimeOffModalOpen] = useState<boolean>(false);
  const [timeOffData, setTimeOffData] = useState<unknown>(null);
  const [addDeductionOpen, setAddDeductionOpen] = useState<boolean>(false);
  const [dentalCodeOpen, setDentalCodeOpen] = useState<boolean>(false);
  const [dentalCodeCurrent, setDentalCodeCurrent] = useState<unknown>(null);
  const [personalInfoModalOpen, setPersonalInfoModalOpen] = useState<boolean>(false);
  const [emergencyContactsModalOpen, setEmergencyContactsModalOpen] = useState<boolean>(false);
  const [employmentModalOpen, setEmploymentModalOpen] = useState<boolean>(false);
  const [employmentLocations, setEmploymentLocations] = useState<unknown[]>([]);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState<boolean>(false);

  useModalEvents({
    onOpenBasePay: () => setBasePayModalOpen(true),
    onOpenTax: () => setTaxModalOpen(true),
    onOpenAddPay: () => setAddPayModalOpen(true),
    onPayItemsLoaded: (ids) => setAssignedPayTypeIds(ids),
    onOpenEditPay: (item) => { setEditingPayItem(item); setEditPayModalOpen(true); },
    onOpenStatHoliday: (data) => { setStatHolidayData(data); setStatHolidayModalOpen(true); },
    onOpenTimeOff: (data) => { setTimeOffData(data); setTimeOffModalOpen(true); },
    onOpenAddDeduction: () => setAddDeductionOpen(true),
    onOpenDentalCode: (current) => { setDentalCodeCurrent(current); setDentalCodeOpen(true); },
    onUnassignDeduction: (it) => {
      if (!it || !it.id) return;
      const t = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
      fetch(API_URL + '/api/v1/employee-deduction-items/' + it.id, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + t },
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['deduction-items'] });
        invalidateEmployee();
      });
    },
    onOpenPersonalInfo: () => setPersonalInfoModalOpen(true),
    onOpenEmergency: () => setEmergencyContactsModalOpen(true),
    onOpenEmployment: (locations) => { setEmploymentLocations(locations); setEmploymentModalOpen(true); },
    onOpenPaymentMethod: () => setPaymentMethodModalOpen(true),
  });

  // openId → URL sync
  useEffect(() => {
    if (!openId) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('section') !== openId) {
      url.searchParams.set('section', openId);
      window.history.replaceState({}, '', url);
    }
  }, [openId]);

  // Load work locations once
  useEffect(() => {
    apiFetch('/api/v1/work-locations', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setWorkLocations(data || []))
      .catch(() => undefined);
  }, []);

  // Section from URL on first load
  useEffect(() => {
    const section = searchParams.get('section');
    if (section) setOpenId(section);
  }, [searchParams]);

  // Load company profile for country/province
  useEffect(() => {
    apiFetch('/api/v1/company/profile', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const co = data.company || data;
        const country = co.country || co.address_country;
        const province = co.province_state || co.address_province;
        if (country) setCompanyCountry(country);
        if (province) setCompanyProvince(province);
      })
      .catch(() => undefined);
  }, []);

  const country: Country = useMemo(() => getCountryConfigAny(companyCountry), [companyCountry]);
  const sections = useMemo(() => buildSections(country), [country]);

  useEffect(() => {
    if (employeeError) setLoadError(employeeError.message);
  }, [employeeError]);

  useEffect(() => {
    if (employee) setValues(employeeToValues(employee, country));
  }, [employee, country]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const startEdit = (sid: string): void => {
    const section = sections.find((s) => s.id === sid);
    const seeded: Values = { ...values };
    if (section) {
      section.fields.forEach((f) => {
        if (!isFilled(seeded[f.k]) && f.default != null) seeded[f.k] = f.default;
        if (!isFilled(seeded[f.k]) && (f.k === 'provinceEmp' || f.k === 'province') && companyProvince) {
          seeded[f.k] = companyProvince;
        }
      });
    }
    setDraft(seeded);
    setFieldErrors({});
    setEditingId(sid);
    setOpenId(sid);
  };

  const cancelEdit = (): void => { setEditingId(null); setDraft({}); setFieldErrors({}); };

  const setOpen = (sid: string): void => {
    if (editingId) return;
    setBasePayModalOpen(false);
    setTaxModalOpen(false);
    setAddPayModalOpen(false);
    setEditPayModalOpen(false);
    setStatHolidayModalOpen(false);
    setTimeOffModalOpen(false);
    setAddDeductionOpen(false);
    setDentalCodeOpen(false);
    setOpenId((cur) => (cur === sid ? null : sid));
    setTimeout(() => {
      const idx = sections.findIndex((x) => x.id === sid);
      if (idx < 0) return;
      const allCards = document.querySelectorAll('.novala-section-stack > *');
      if (allCards[idx]) (allCards[idx] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const onFieldChange = (k: string, val: unknown): void => {
    setDraft((d) => ({ ...d, [k]: val }));
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: null }));
  };

  const saveSection = async (sid: string): Promise<void> => {
    const section = sections.find((s) => s.id === sid);
    if (!section) return;
    const visibleFields = section.fields.filter((f) => !f.showIf || f.showIf(draft));
    const errs: Record<string, string> = {};
    visibleFields.forEach((f) => {
      const err = validateFieldAny(f, draft[f.k]);
      if (err) errs[f.k] = err;
    });
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setToast({ kind: 'err', msg: 'Please fix the errors below' });
      return;
    }

    setSavingId(sid);
    try {
      const patch = valuesToPatch(sid, draft, employee, country);
      const res = await apiFetch('/api/v1/payroll/employees/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error('Save failed (' + res.status + '): ' + txt.slice(0, 200));
      }
      const updated = await res.json();
      const emp: Employee = updated.employee || updated;
      setEmployee(emp);
      setEditingId(null);
      setDraft({});
      setFieldErrors({});
      setToast({ kind: 'ok', msg: 'Saved ' + section.title.toLowerCase() });
    } catch (err: any) {
      setToast({ kind: 'err', msg: err.message });
    } finally {
      setSavingId(null);
    }
  };

  const allRequiredDone = useMemo(() => {
    if (!employee) return false;
    const readiness = getReadinessAny(employee);
    return Boolean(readiness && readiness.ready);
  }, [employee]);

  if (employeeLoading) {
    return <div style={{ padding: 40, fontFamily: FONT, color: C.muted, textAlign: 'center' }}>Loading employee...</div>;
  }
  if (loadError) {
    return (
      <div style={{ padding: 40, fontFamily: FONT }}>
        <div style={{ background: '#FEE2E2', border: '1px solid #F87171', color: '#991B1B', padding: 16, borderRadius: 10 }}>
          Could not load employee: {loadError}
        </div>
        <button
          onClick={() => navigate('/payroll/employees')}
          style={{
            marginTop: 16, fontFamily: FONT, fontWeight: 600, fontSize: 14,
            color: C.tealInk, background: 'none', border: 0, cursor: 'pointer',
          }}
        >
          Back to Employees
        </button>
      </div>
    );
  }

  const employeeWithId = { ...(employee || {}), ...(values || {}), id };

  return (
    <div style={{
      background: C.surface, minHeight: '100vh', fontFamily: FONT, color: C.text,
      padding: '28px 32px 100px', maxWidth: '100%', margin: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#12262B', marginBottom: 14 }}>
        <span style={{ fontWeight: 600, opacity: 0.7, cursor: 'pointer' }} onClick={() => navigate('/payroll/overview')}>Payroll</span>
        <span style={{ opacity: 0.4 }}>/</span>
        <span style={{ fontWeight: 600, opacity: 0.7, cursor: 'pointer' }} onClick={() => navigate('/payroll/employees')}>Employees</span>
        <span style={{ opacity: 0.4 }}>/</span>
        <span style={{ fontWeight: 700 }}>{values.name || 'Employee'}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 20 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: C.tealSoft, color: C.tealInk,
          display: 'grid', placeItems: 'center',
          fontWeight: 600, fontSize: 22, flex: '0 0 64px',
        }}>
          {initialsFrom(values.name)}
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', margin: 0 }}>
              {values.name || 'Unnamed employee'}
            </h1>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 700,
              padding: '3px 10px', borderRadius: 5,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              background: allRequiredDone ? C.greenSoft : C.amberSoft,
              color: allRequiredDone ? C.green : C.amber,
            }}>
              <span style={{ width: 6, height: 6, background: allRequiredDone ? C.green : C.amber, borderRadius: '50%' }} />
              {allRequiredDone ? 'Active' : 'Setup incomplete'}
            </span>
          </div>
          <div style={{ fontSize: 13.5, color: C.muted, marginTop: 2 }}>Employee, {country.name}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '262px 1fr', gap: 26, alignItems: 'start' }}>
        <Rail sections={sections} values={values} openId={openId} onPick={setOpen} editingId={editingId} />
        <div className="novala-section-stack" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sections.map((s) => (
            <Section
              key={s.id}
              workLocations={workLocations}
              section={s}
              values={values}
              draft={draft}
              country={country}
              employeeId={id}
              fullEmployee={employee}
              isOpen={openId === s.id}
              isEditing={editingId === s.id}
              isSaving={savingId === s.id}
              disabledByOtherEdit={Boolean(editingId) && editingId !== s.id}
              fieldErrors={fieldErrors}
              onToggleOpen={() => setOpen(s.id)}
              onEdit={() => startEdit(s.id)}
              onCancel={cancelEdit}
              onSave={() => saveSection(s.id)}
              onChange={onFieldChange}
            />
          ))}
        </div>
      </div>

      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#FFFFFF', padding: '16px 32px',
        borderTop: '1px solid #E7EAF0',
        boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        zIndex: 90,
      }}>
        <button
          onClick={() => navigate('/payroll/employees')}
          style={{
            padding: '12px 18px', background: '#FFFFFF',
            border: '1.5px solid #12262B', borderRadius: 10,
            color: '#12262B', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: FONT,
          }}
        >
          ← Back to Employees
        </button>
        <div style={{ fontSize: 12, color: '#12262B', opacity: 0.7 }}>
          {allRequiredDone ? 'All required sections complete' : 'Some sections still need to be filled'}
        </div>
      </div>

      <BasePayModalAny isOpen={basePayModalOpen} employee={employeeWithId} onClose={() => setBasePayModalOpen(false)} onSaved={() => { setBasePayModalOpen(false); invalidateEmployee(); }} />
      <TaxWithholdingsModalAny isOpen={taxModalOpen} employee={employeeWithId} onClose={() => setTaxModalOpen(false)} onSaved={() => { setTaxModalOpen(false); invalidateEmployee(); }} />
      <PayTypesDrawerAny open={addPayModalOpen} onClose={() => { setAddPayModalOpen(false); invalidateEmployee(); }} employeeId={id} />
      <EditPayTypeModalAny isOpen={editPayModalOpen} item={editingPayItem} employee={employeeWithId} onClose={() => { setEditPayModalOpen(false); setEditingPayItem(null); }} onSaved={() => { setEditPayModalOpen(false); setEditingPayItem(null); invalidateEmployee(); }} />
      <StatHolidayModalAny isOpen={statHolidayModalOpen} employee={employeeWithId} data={statHolidayData} onClose={() => setStatHolidayModalOpen(false)} onSaved={() => { setStatHolidayModalOpen(false); invalidateEmployee(); }} />
      <TimeOffModalAny isOpen={timeOffModalOpen} employee={employeeWithId} data={timeOffData} onClose={() => setTimeOffModalOpen(false)} onSaved={() => { setTimeOffModalOpen(false); queryClient.invalidateQueries({ queryKey: ['time-off'] }); invalidateEmployee(); }} />
      <AddDeductionModalAny isOpen={addDeductionOpen} employee={employeeWithId} onClose={() => setAddDeductionOpen(false)} onSaved={() => { setAddDeductionOpen(false); invalidateEmployee(); }} />
      <DentalT4CodeModalAny isOpen={dentalCodeOpen} employee={employeeWithId} current={dentalCodeCurrent} onClose={() => setDentalCodeOpen(false)} onSaved={() => { setDentalCodeOpen(false); invalidateEmployee(); }} />
      <PersonalInfoModalAny isOpen={personalInfoModalOpen} employee={employeeWithId} onClose={() => setPersonalInfoModalOpen(false)} onSaved={() => { setPersonalInfoModalOpen(false); invalidateEmployee(); }} />
      <EmergencyContactsModalAny isOpen={emergencyContactsModalOpen} employee={employeeWithId} onClose={() => setEmergencyContactsModalOpen(false)} onSaved={() => { setEmergencyContactsModalOpen(false); invalidateEmployee(); }} />
      <EmploymentDetailsModalAny isOpen={employmentModalOpen} employee={employeeWithId} locations={employmentLocations} onClose={() => setEmploymentModalOpen(false)} onSaved={() => { setEmploymentModalOpen(false); invalidateEmployee(); }} />
      <PaymentMethodModalAny isOpen={paymentMethodModalOpen} employee={employeeWithId} onClose={() => setPaymentMethodModalOpen(false)} onSaved={() => { setPaymentMethodModalOpen(false); invalidateEmployee(); }} />

      {toast && (
        <div style={{
          position: 'fixed', bottom: 26, left: '50%',
          transform: 'translateX(-50%)',
          background: toast.kind === 'err' ? '#7F1D1D' : C.ink,
          color: '#fff', fontSize: 13, fontWeight: 500,
          padding: '11px 18px', borderRadius: 10,
          zIndex: 80, display: 'flex', alignItems: 'center', gap: 9,
          boxShadow: '0 8px 24px rgba(16,26,43,0.3)',
        }}>
          {toast.kind === 'ok'
            ? <Check size={16} color="#7FE3D2" />
            : <span style={{ color: '#FCA5A5', fontWeight: 700 }}>!</span>}
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}